import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    FileEdit, Package, Building2, ChevronDown, Loader2,
    Search, Upload, CheckCircle, AlertTriangle,
    X, RefreshCw, Send, IndianRupee, FileText, Eye,
} from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';
import { S3_FOLDERS, buildSupplierPOUrl, buildSupplierPOAmendUrl, getFileName } from '../../config/s3Config';
import { formatIndianCurrency } from '../../utilities/amountToTextHelper';

import {
    fetchAmendCCList,
    fetchAmendPOList,
    fetchAmendItemGrid,
    fetchAmendUploadDocValue,
    fetchAmendUploadedDocs,
    checkAmendBudget,
    submitSupplierPOAmend,
    fetchAmendPreviousPODetails,
    uploadAmendDocument,
    clearAmendPOList,
    clearAmendItemGrid,
    clearAmendUploadedDocs,
    clearAmendBudgetResult,
    clearAmendSaveResult,
    clearPreviousPODetails,
    resetSupplierPOAmendCreation,
    selectAmendCCList,
    selectAmendPOList,
    selectAmendItemGrid,
    selectAmendUploadDocValue,
    selectAmendUploadedDocs,
    selectAmendSaveResult,
    selectAmendPreviousPODetails,
    selectAmendCreationLoading,
    selectAmendCreationErrors,
} from '../../slices/supplierPOSlice/supplierPOAmendCreationSlice';

// ── Shared UI primitives (mirrors SupplierPOCreation conventions) ──────────────

const inputCls =
    'w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-800 ' +
    'text-gray-800 dark:text-gray-100 border-gray-300 dark:border-gray-600 ' +
    'focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 ' +
    'disabled:opacity-60 disabled:cursor-not-allowed transition-colors';

const Label = ({ children, required }) => (
    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
        {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
);

const SectionCard = ({ icon: Icon, title, subtitle, children, accent = 'indigo' }) => {
    const colors = {
        indigo: 'from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20 border-indigo-200 dark:border-indigo-700 text-indigo-800 dark:text-indigo-300',
        green:  'from-green-50  to-green-100  dark:from-green-900/20  dark:to-green-800/20  border-green-200  dark:border-green-700  text-green-800  dark:text-green-300',
        purple: 'from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-purple-200 dark:border-purple-700 text-purple-800 dark:text-purple-300',
        amber:  'from-amber-50  to-amber-100  dark:from-amber-900/20  dark:to-amber-800/20  border-amber-200  dark:border-amber-700  text-amber-800  dark:text-amber-300',
        rose:   'from-rose-50   to-rose-100   dark:from-rose-900/20   dark:to-rose-800/20   border-rose-200   dark:border-rose-700   text-rose-800   dark:text-rose-300',
    };
    const cls = colors[accent] || colors.indigo;
    return (
        <div className={`bg-gradient-to-br ${cls} rounded-xl border p-5 space-y-4`}>
            <div className="flex items-center gap-2">
                <Icon className="w-4 h-4" />
                <h3 className="font-semibold text-sm">{title}</h3>
                {subtitle && <span className="text-xs opacity-70 ml-1">{subtitle}</span>}
            </div>
            {children}
        </div>
    );
};

const SpinnerIcon = () => <Loader2 className="w-4 h-4 animate-spin" />;

// Extract CCType from the menu path string, e.g. /Purchase/SupplierpoAmend?CCType=NPCC
const parseCCType = (menuData) => {
    const path = menuData?.path || '';
    const m = path.match(/[?&]CCType=([^&]+)/i);
    if (m) return m[1].toUpperCase();
    if (path.toLowerCase().includes('npcc')) return 'NPCC';
    return 'PCC';
};

const round2 = (v) => Math.round((parseFloat(v) || 0) * 100) / 100;

const todayFormatted = () => {
    const d = new Date();
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${String(d.getDate()).padStart(2,'0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
};

const isNewItemType = (itemType) => (itemType || '').toLowerCase() !== 'existing';

// ── Main Component ──────────────────────────────────────────────────────────

const SupplierPOAmend = ({ menuData }) => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);

    const roleId = userData?.roleId || userData?.RID || '';
    const userId = userData?.UID   || userData?.uid  || '';
    const ccType = parseCCType(menuData);

    const ccList          = useSelector(selectAmendCCList);
    const poList           = useSelector(selectAmendPOList);
    const itemGrid          = useSelector(selectAmendItemGrid);
    const uploadDocValue    = useSelector(selectAmendUploadDocValue);
    const uploadedDocs      = useSelector(selectAmendUploadedDocs);
    const saveResult        = useSelector(selectAmendSaveResult);
    const previousPODetails = useSelector(selectAmendPreviousPODetails);
    const loading           = useSelector(selectAmendCreationLoading);
    const errors            = useSelector(selectAmendCreationErrors);

    // ── Local state ──────────────────────────────────────────────────────────
    const [selectedCC, setSelectedCC] = useState('');
    const [selectedPO, setSelectedPO] = useState('');
    const [rowEdits, setRowEdits]     = useState({});
    const [remarks, setRemarks]       = useState('');
    const [amendDate, setAmendDate]   = useState(todayFormatted());
    const [qcFile, setQcFile]         = useState(null);
    const [budgetPassed, setBudgetPassed] = useState(false);
    const [previewItem, setPreviewItem] = useState(null);
    const [showPreviousModal, setShowPreviousModal] = useState(false);

    // ── Init ─────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (userId && roleId) {
            dispatch(fetchAmendCCList({ userId, roleId, ccType }));
        }
        return () => dispatch(resetSupplierPOAmendCreation());
    }, [userId, roleId, ccType, dispatch]);

    // Sync row edits when the item grid arrives
    useEffect(() => {
        if (itemGrid?.lstItems?.length) {
            const initial = {};
            itemGrid.lstItems.forEach((item) => {
                initial[item.IndentListId] = {
                    checked: false,
                    amendType: '',
                    qty: '',
                    quotedPrice: item.POQuotedPrice || 0,
                    purchasePrice: item.POPurchasePrice || 0,
                    remarks: '',
                };
            });
            setRowEdits(initial);
            setBudgetPassed(false);
        }
    }, [itemGrid]);

    // React to save result
    useEffect(() => {
        if (!saveResult) return;
        const parts = String(saveResult).split('$');
        const [status, amendPONo, filePath] = parts;

        if (status === 'Submited') {
            const finishUp = () => {
                toast.success(`Amend PO Number ${amendPONo} created successfully.`);
                handleReset();
                if (selectedCC) dispatch(fetchAmendPOList({ ccCode: selectedCC, userId, roleId }));
            };

            if (qcFile && filePath) {
                dispatch(uploadAmendDocument({
                    file: qcFile,
                    fileName: filePath,
                    folder: S3_FOLDERS.SUPPLIER_PO_AMENDMENTS,
                })).then(() => finishUp());
            } else {
                finishUp();
            }
        } else {
            toast.error(saveResult || 'Failed to save Supplier PO amendment');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveResult]);

    // ── Handlers: CC / PO selection ─────────────────────────────────────────

    const handleCCChange = (cc) => {
        setSelectedCC(cc);
        setSelectedPO('');
        dispatch(clearAmendItemGrid());
        dispatch(clearAmendUploadedDocs());
        dispatch(clearAmendPOList());
        setRowEdits({});
        setBudgetPassed(false);
        if (cc) {
            dispatch(fetchAmendPOList({ ccCode: cc, userId, roleId }));
            dispatch(fetchAmendUploadDocValue(cc));
        }
    };

    const handlePOChange = (poNo) => {
        setSelectedPO(poNo);
        dispatch(clearAmendItemGrid());
        dispatch(clearAmendUploadedDocs());
        setBudgetPassed(false);
        if (poNo && selectedCC) {
            dispatch(fetchAmendItemGrid({ ccCode: selectedCC, poNo }));
            dispatch(fetchAmendUploadedDocs({ poNo, forType: 'Supplier' }));
        }
    };

    const handleReset = () => {
        setSelectedCC('');
        setSelectedPO('');
        setRowEdits({});
        setRemarks('');
        setAmendDate(todayFormatted());
        setQcFile(null);
        setBudgetPassed(false);
        dispatch(clearAmendItemGrid());
        dispatch(clearAmendUploadedDocs());
        dispatch(clearAmendPOList());
        dispatch(clearAmendBudgetResult());
        dispatch(clearAmendSaveResult());
        if (userId && roleId) dispatch(fetchAmendCCList({ userId, roleId, ccType }));
    };

    // ── Row edit handlers ────────────────────────────────────────────────────

    const items = useMemo(() => itemGrid?.lstItems || [], [itemGrid]);
    const poData = useMemo(() => itemGrid?.POData || {}, [itemGrid]);

    const toggleRowChecked = (item) => {
        const id = item.IndentListId;
        setBudgetPassed(false);
        setRowEdits((prev) => {
            const current = prev[id] || {};
            const nextChecked = !current.checked;
            return {
                ...prev,
                [id]: {
                    ...current,
                    checked: nextChecked,
                    ...(nextChecked ? {} : { amendType: '', qty: '', remarks: current.remarks }),
                },
            };
        });
    };

    const handleAmendTypeChange = (item, value) => {
        setBudgetPassed(false);
        setRowEdits((prev) => ({
            ...prev,
            [item.IndentListId]: {
                ...prev[item.IndentListId],
                amendType: value,
                qty: '',
                // Picking an Amend Type is the user's intent to include this row —
                // auto-check it so they don't have to tick the checkbox first.
                checked: value ? true : prev[item.IndentListId]?.checked,
            },
        }));
    };

    const handleQtyChange = (item, value) => {
        setBudgetPassed(false);
        const edit = rowEdits[item.IndentListId] || {};
        const numVal = parseFloat(value);
        if (edit.amendType === 'Add') {
            const cap = parseFloat(item.Requestedqty) || 0;
            if (!isNaN(numVal) && numVal > cap) {
                toast.error(`You cannot Add quantity more than Indent quantity (${cap})`);
                value = String(cap);
            }
        } else if (edit.amendType === 'Substract') {
            const cap = parseFloat(item.RemainingQty) || 0;
            if (!isNaN(numVal) && numVal > cap) {
                toast.error(`You cannot Substract quantity more than PO current quantity (${cap})`);
                value = String(cap);
            }
        }
        setRowEdits((prev) => ({ ...prev, [item.IndentListId]: { ...prev[item.IndentListId], qty: value } }));
    };

    const handleFieldChange = (item, field, value) => {
        setBudgetPassed(false);
        setRowEdits((prev) => ({ ...prev, [item.IndentListId]: { ...prev[item.IndentListId], [field]: value } }));
    };

    // ── Computed totals (mirrors legacy CountSupplierPOAmount()) ────────────

    const totals = useMemo(() => {
        let plusTotal = 0, minusTotal = 0;      // standard-vs-purchase price variance (drives budget check)
        let poPlusAmt = 0, poMinusAmt = 0;      // raw PO value change (drives revised PO value)

        items.forEach((item) => {
            const e = rowEdits[item.IndentListId];
            if (!e || !e.checked || !e.amendType) return;
            const qty = parseFloat(e.qty) || 0;
            const purchasePrice = parseFloat(e.purchasePrice) || 0;
            const standardPrice = parseFloat(item.basicprice) || 0;
            if (qty <= 0 || purchasePrice <= 0) return;

            const rowAmount = qty * purchasePrice;
            const rowStandardAmount = qty * standardPrice;

            if (e.amendType === 'Add') {
                poPlusAmt += rowAmount;
                if (standardPrice < purchasePrice) plusTotal += (rowAmount - rowStandardAmount);
                else if (standardPrice > purchasePrice) minusTotal += (rowStandardAmount - rowAmount);
            } else if (e.amendType === 'Substract') {
                poMinusAmt += rowAmount;
                if (standardPrice < purchasePrice) minusTotal += (rowAmount - rowStandardAmount);
                else if (standardPrice > purchasePrice) plusTotal += (rowStandardAmount - rowAmount);
            }
        });

        const amendDiffValue = Math.abs(poPlusAmt - poMinusAmt);
        const oldPOValue = parseFloat(poData.NewpricePOTotal) || 0;
        const revisedValue = oldPOValue + poPlusAmt - poMinusAmt;
        const checkValue = Math.round((poPlusAmt - poMinusAmt) * 100) / 100;
        const reduceBudgetAmt = plusTotal > minusTotal ? (plusTotal - minusTotal) : 0;

        return { plusTotal, minusTotal, poPlusAmt, poMinusAmt, amendDiffValue, oldPOValue, revisedValue, checkValue, reduceBudgetAmt };
    }, [items, rowEdits, poData]);

    const selectedItems = useMemo(
        () => items.filter((item) => rowEdits[item.IndentListId]?.checked),
        [items, rowEdits]
    );

    const uploadRequired = totals.checkValue > (parseFloat(uploadDocValue) || 0);

    // ── Previous PO details popup ───────────────────────────────────────────

    const handleShowPrevious = (item) => {
        setPreviewItem(item);
        setShowPreviousModal(true);
        dispatch(fetchAmendPreviousPODetails(item.itemcode));
    };

    const closePreviousModal = () => {
        setShowPreviousModal(false);
        setPreviewItem(null);
        dispatch(clearPreviousPODetails());
    };

    // ── Budget check ─────────────────────────────────────────────────────────

    const handleBudgetCheck = async () => {
        if (!selectedItems.length) { toast.warning('Please select at least one item'); return; }
        const ids = selectedItems.map((i) => i.IndentListId).join(',') + ',';

        const result = await dispatch(checkAmendBudget({
            Indentlistids: ids,
            ReducedBudgetAmount: totals.reduceBudgetAmt,
            IndentNo: poData.IndentNo || '',
        }));

        if (checkAmendBudget.fulfilled.match(result)) {
            const payload = result.payload;
            if (payload === 'Sufficient') {
                toast.success('Budget check passed');
                setBudgetPassed(true);
            } else {
                toast.error(payload || 'Budget check failed');
                setBudgetPassed(false);
            }
        }
    };

    // ── Submit ───────────────────────────────────────────────────────────────

    const buildCsvFields = () => {
        let ids = '', codes = '', itemTypes = '', poQtys = '', recQtys = '', indentQtys = '',
            quotedPrices = '', standardPrices = '', amendTypes = '', amendQtys = '', purchasePrices = '',
            purchasePriceAmts = '', standardPriceAmts = '', itemRemarks = '', remainingQtys = '';

        selectedItems.forEach((item) => {
            const e = rowEdits[item.IndentListId];
            const qty = parseFloat(e.qty) || 0;
            const purchasePrice = parseFloat(e.purchasePrice) || 0;
            const standardPrice = parseFloat(item.basicprice) || 0;

            ids               += `${item.IndentListId},`;
            codes             += `${item.itemcode},`;
            itemTypes         += `${item.ItemType},`;
            poQtys            += `${item.CurrentQty},`;
            recQtys           += `${item.RecQty},`;
            indentQtys        += `${item.Requestedqty},`;
            quotedPrices      += `${e.quotedPrice || 0},`;
            standardPrices    += `${standardPrice},`;
            amendTypes        += `${e.amendType},`;
            amendQtys         += `${qty},`;
            purchasePrices    += `${purchasePrice},`;
            purchasePriceAmts += `${(qty * purchasePrice).toFixed(2)},`;
            standardPriceAmts += `${(qty * standardPrice).toFixed(2)},`;
            itemRemarks       += `${e.remarks?.trim() || 'none'},`;
            remainingQtys     += `${item.RemainingQty},`;
        });

        return { ids, codes, itemTypes, poQtys, recQtys, indentQtys, quotedPrices, standardPrices, amendTypes, amendQtys, purchasePrices, purchasePriceAmts, standardPriceAmts, itemRemarks, remainingQtys };
    };

    const validate = () => {
        if (!selectedCC) { toast.error(`Select ${ccType === 'NPCC' ? 'Job' : 'Cost Center'} Code`); return false; }
        if (!selectedPO) { toast.error('Select PO Number'); return false; }
        if (!remarks.trim()) { toast.error('Enter Remarks'); return false; }
        if (!amendDate) { toast.error('Select Amend Date'); return false; }
        if (!selectedItems.length) { toast.error('Select at least one item'); return false; }

        for (const item of selectedItems) {
            const e = rowEdits[item.IndentListId];
            if (!e.amendType) { toast.error(`Select Amend Type for ${item.itemcode?.trim()}`); return false; }
            const qty = parseFloat(e.qty) || 0;
            if (qty <= 0) { toast.error(`Enter Amend Quantity for ${item.itemcode?.trim()}`); return false; }
            if (!parseFloat(e.purchasePrice)) { toast.error(`Enter Purchase Price for ${item.itemcode?.trim()}`); return false; }
            if (!parseFloat(e.quotedPrice)) { toast.error(`Enter Quoted Price for ${item.itemcode?.trim()}`); return false; }
            if (e.amendType === 'Add' && qty > (parseFloat(item.Requestedqty) || 0)) {
                toast.error(`Amend Quantity exceeds Indent Quantity for ${item.itemcode?.trim()}`); return false;
            }
            if (e.amendType === 'Substract' && qty > (parseFloat(item.RemainingQty) || 0)) {
                toast.error(`Amend Quantity exceeds available PO quantity for ${item.itemcode?.trim()}`); return false;
            }
        }

        if (uploadRequired) {
            if (!qcFile) { toast.error('Upload QCS PDF File — amendment value exceeds the permitted limit'); return false; }
            if (getFileExt(qcFile.name) !== 'pdf') { toast.error('Invalid upload file. Only PDF is accepted'); return false; }
        }
        return true;
    };

    const getFileExt = (name) => (name || '').split('.').pop().toLowerCase();

    const handleSubmit = async () => {
        if (!validate()) return;
        const csv = buildCsvFields();

        const payload = {
            CCCode: selectedCC,
            IndentNo: poData.IndentNo || '',
            PONo: selectedPO,
            VendorCode: poData.VendorCode || '',
            AmendDate: amendDate,
            Remarks: remarks,
            PlusAmount: round2(totals.plusTotal),
            MinusAmount: round2(totals.minusTotal),
            Indentlistids: csv.ids,
            Itemcodes: csv.codes,
            Itemtypes: csv.itemTypes,
            POQtys: csv.poQtys,
            RecievedQtys: csv.recQtys,
            IndentQtys: csv.indentQtys,
            Quotedprices: csv.quotedPrices,
            Standardprices: csv.standardPrices,
            Amendtypes: csv.amendTypes,
            AmendQtys: csv.amendQtys,
            Purchaseprices: csv.purchasePrices,
            PurchasepriceAmts: csv.purchasePriceAmts,
            StandardpriceAmts: csv.standardPriceAmts,
            ItemRemarks: csv.itemRemarks,
            RemainingQtys: csv.remainingQtys,
            // Legacy SaveSupplierPOAmend sends the gross plus/minus totals here (only the budget
            // check uses the net plus − minus), all rounded to 2 decimals as the form shows them.
            ReducedBudgetAmount: round2(totals.plusTotal),
            ReturnBudgetAmount: round2(totals.minusTotal),
            OldPOValue: totals.oldPOValue,
            AmendDiffValue: round2(totals.amendDiffValue),
            RevisedValue: round2(totals.revisedValue),
            AddedPO: round2(totals.poPlusAmt),
            SubstractedPO: round2(totals.poMinusAmt),
            Filechk: qcFile ? 'Yes' : 'No',
            Extension: qcFile ? getFileExt(qcFile.name) : '',
            RoleId: parseInt(roleId) || 0,
            CreatedBy: userData?.userName || "",
        };

        if (totals.reduceBudgetAmt > 0 && !budgetPassed) {
            const result = await dispatch(checkAmendBudget({
                Indentlistids: csv.ids,
                ReducedBudgetAmount: totals.reduceBudgetAmt,
                IndentNo: poData.IndentNo || '',
            }));
            if (checkAmendBudget.fulfilled.match(result) && result.payload === 'Sufficient') {
                setBudgetPassed(true);
                dispatch(submitSupplierPOAmend(payload));
            } else {
                toast.error((checkAmendBudget.fulfilled.match(result) && result.payload) || 'Budget check failed');
            }
            return;
        }

        dispatch(submitSupplierPOAmend(payload));
    };

    // ── Render ───────────────────────────────────────────────────────────────

    const gridHasData = items.length > 0;
    const ccLabel = ccType === 'NPCC' ? 'Job' : 'Cost Center';

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 space-y-5">

            {/* Page Title */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow">
                        <FileEdit className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h1 className="text-lg font-bold text-gray-900 dark:text-white">Supplier PO Amend</h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            {ccLabel} Type: <span className="font-semibold text-indigo-600 dark:text-indigo-400">{ccType}</span>
                        </p>
                    </div>
                </div>
                <button onClick={handleReset}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 transition-colors">
                    <RefreshCw className="w-3.5 h-3.5" /> Reset
                </button>
            </div>

            {/* ── 1. CC / PO Selection ─────────────────────────────────────── */}
            <SectionCard icon={Building2} title={`${ccLabel} & PO Selection`} accent="indigo">
                <div className="flex items-end gap-3 flex-wrap">
                    <div className="flex-1 min-w-[240px]">
                        <Label required>{ccLabel}</Label>
                        <div className="relative">
                            <select className={inputCls} value={selectedCC} onChange={(e) => handleCCChange(e.target.value)}>
                                <option value="">-- Please Select --</option>
                                {ccList.map((cc) => (
                                    <option key={cc.CC_Id} value={cc.CC_Code}>{cc.CC_Name}</option>
                                ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                {loading.ccList ? <SpinnerIcon /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                            </div>
                        </div>
                    </div>

                    <div className="flex-1 min-w-[240px]">
                        <Label required>PO No</Label>
                        <div className="relative">
                            <select className={inputCls} value={selectedPO} disabled={!selectedCC}
                                onChange={(e) => handlePOChange(e.target.value)}>
                                <option value="">-- Please Select --</option>
                                {poList.map((po) => (
                                    <option key={po.Id} value={po.PONo}>{po.PONo}</option>
                                ))}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                {loading.poList ? <SpinnerIcon /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                            </div>
                        </div>
                    </div>

                    {loading.itemGrid && (
                        <div className="flex items-center gap-2 text-sm text-indigo-600"><SpinnerIcon /> Loading items…</div>
                    )}

                    {gridHasData && itemGrid?.SerialNo != null && (
                        <div className="flex items-center gap-1.5 text-xs text-green-700 dark:text-green-400 font-medium">
                            <CheckCircle className="w-3.5 h-3.5" /> Amendment No: {itemGrid.SerialNo}
                        </div>
                    )}
                </div>
            </SectionCard>

            {/* ── 2. PO Header (readonly reference info) ──────────────────── */}
            {gridHasData && (
                <SectionCard icon={FileText} title="PO Reference" accent="green">
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                        <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 font-semibold uppercase tracking-wide mb-0.5">Vendor Name</p>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{poData.VendorName || '—'}</p>
                        </div>
                        <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 font-semibold uppercase tracking-wide mb-0.5">Indent No</p>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{poData.IndentNo || '—'}</p>
                        </div>
                        <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 font-semibold uppercase tracking-wide mb-0.5">PO Date</p>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{poData.PODate || '—'}</p>
                        </div>
                        <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 font-semibold uppercase tracking-wide mb-0.5">PO Expire Date</p>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{poData.POExpireDate || '—'}</p>
                        </div>
                        <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 font-semibold uppercase tracking-wide mb-0.5">Ref No / Date</p>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{poData.RefNo || '—'} {poData.RefDate ? `(${poData.RefDate})` : ''}</p>
                        </div>
                        <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 font-semibold uppercase tracking-wide mb-0.5">MRR Type</p>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{poData.MRRType === 'YES' ? 'Single' : 'Multiple'}</p>
                        </div>
                    </div>

                    {uploadedDocs?.length > 0 && (
                        <div className="pt-2 border-t border-green-200 dark:border-green-800">
                            <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">Previously uploaded documents:</p>
                            <div className="flex flex-wrap gap-2">
                                {uploadedDocs.filter((d) => d.Path).map((d, i) => (
                                    <a key={i} href={(d.POType === 'Amend' ? buildSupplierPOAmendUrl : buildSupplierPOUrl)(d.Path)}
                                        target="_blank" rel="noreferrer"
                                        className="flex items-center gap-1.5 px-2.5 py-1 bg-white dark:bg-gray-800 border border-green-300 dark:border-green-700 rounded-lg text-xs text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20">
                                        <FileText className="w-3 h-3" /> {getFileName(d.Path)}
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}
                </SectionCard>
            )}

            {/* ── 3. Items Grid ────────────────────────────────────────────── */}
            {gridHasData && (
                <SectionCard icon={Package} title="Item Details"
                    subtitle={`${selectedItems.length} of ${items.length} item${items.length !== 1 ? 's' : ''} selected`}
                    accent="purple">

                    <div className="overflow-x-auto rounded-lg border border-purple-200 dark:border-purple-700">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
                                    {['', 'Item', 'Unit', 'Type', 'Prev. Qty', 'Avail. to Add', 'Allowed Substract',
                                      'Quoted Price*', 'Std. Price', 'Amend Type*', 'Amend Qty*', 'Purchase Price*',
                                      'Amount', 'Remarks', 'HSN'].map((h, i) => (
                                        <th key={i} className="px-2.5 py-2.5 text-left font-semibold whitespace-nowrap">{h}</th>
                                    ))}
                                    {poData.GSTExist === 'Yes' && poData.StateStatus === 'Yes' && (
                                        <>
                                            <th className="px-2.5 py-2.5 text-left font-semibold whitespace-nowrap">CGST%</th>
                                            <th className="px-2.5 py-2.5 text-left font-semibold whitespace-nowrap">SGST%</th>
                                        </>
                                    )}
                                    {poData.GSTExist === 'Yes' && poData.StateStatus !== 'Yes' && (
                                        <th className="px-2.5 py-2.5 text-left font-semibold whitespace-nowrap">IGST%</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item, idx) => {
                                    const canAdd = (parseFloat(item.Requestedqty) || 0) > 0;
                                    const canSubstract = (parseFloat(item.RemainingQty) || 0) > 0;
                                    if (!canAdd && !canSubstract) return null;

                                    const e = rowEdits[item.IndentListId] || {};
                                    const isNew = isNewItemType(item.ItemType);
                                    const qty = parseFloat(e.qty) || 0;
                                    const purchasePrice = parseFloat(e.purchasePrice) || 0;
                                    const amount = qty * purchasePrice;
                                    const row = idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-purple-50/40 dark:bg-purple-900/10';

                                    return (
                                        <tr key={item.IndentListId} className={`${row} border-b border-purple-100 dark:border-purple-800 last:border-0`}>
                                            <td className="px-2.5 py-2">
                                                <input type="checkbox" className="w-4 h-4 accent-indigo-600 cursor-pointer"
                                                    checked={!!e.checked} onChange={() => toggleRowChecked(item)} />
                                            </td>
                                            <td className="px-2.5 py-2 max-w-[200px]">
                                                <div className="flex items-start gap-1.5">
                                                    <button type="button" onClick={() => handleShowPrevious(item)}
                                                        className="mt-0.5 p-0.5 rounded hover:bg-indigo-100 dark:hover:bg-indigo-900/30 text-indigo-500 shrink-0"
                                                        title="View previous PO history">
                                                        <Search className="w-3 h-3" />
                                                    </button>
                                                    <div className="min-w-0">
                                                        <p className="font-mono text-gray-800 dark:text-gray-200 whitespace-nowrap">{item.itemcode?.trim()}</p>
                                                        <p className="font-medium text-gray-900 dark:text-white truncate">{item.itemname}</p>
                                                        {item.specification && <p className="text-gray-500 dark:text-gray-400 text-[10px] truncate">{item.specification}</p>}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-2.5 py-2 text-gray-700 dark:text-gray-300">{item.units}</td>
                                            <td className="px-2.5 py-2">
                                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${isNew ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}>
                                                    {item.ItemType}
                                                </span>
                                            </td>
                                            <td className="px-2.5 py-2 text-right text-gray-800 dark:text-gray-200">{item.CurrentQty}</td>
                                            <td className="px-2.5 py-2 text-right text-gray-800 dark:text-gray-200">{item.Requestedqty}</td>
                                            <td className="px-2.5 py-2 text-right text-gray-800 dark:text-gray-200">{item.RemainingQty}</td>

                                            {/* Quoted Price */}
                                            <td className="px-1.5 py-1.5">
                                                <input type="number" min="0" step="0.01" disabled={!isNew}
                                                    className={`${inputCls} w-24 text-right`}
                                                    value={e.quotedPrice ?? ''}
                                                    onChange={(ev) => handleFieldChange(item, 'quotedPrice', ev.target.value)} />
                                            </td>

                                            <td className="px-2.5 py-2 text-right font-medium text-indigo-700 dark:text-indigo-400 whitespace-nowrap">
                                                ₹{formatIndianCurrency(item.basicprice || 0)}
                                            </td>

                                            {/* Amend Type */}
                                            <td className="px-1.5 py-1.5">
                                                <select className={`${inputCls} w-28`}
                                                    value={e.amendType || ''}
                                                    onChange={(ev) => handleAmendTypeChange(item, ev.target.value)}>
                                                    <option value="">Select</option>
                                                    {canAdd && <option value="Add">Add</option>}
                                                    {canSubstract && <option value="Substract">Substract</option>}
                                                </select>
                                            </td>

                                            {/* Amend Qty */}
                                            <td className="px-1.5 py-1.5">
                                                <input type="number" min="0" step="0.01" disabled={!e.amendType}
                                                    className={`${inputCls} w-20 text-right`}
                                                    value={e.qty ?? ''}
                                                    onChange={(ev) => handleQtyChange(item, ev.target.value)} />
                                            </td>

                                            {/* Purchase Price */}
                                            <td className="px-1.5 py-1.5">
                                                <input type="number" min="0" step="0.01" disabled={!isNew}
                                                    className={`${inputCls} w-24 text-right`}
                                                    value={e.purchasePrice ?? ''}
                                                    onChange={(ev) => handleFieldChange(item, 'purchasePrice', ev.target.value)} />
                                            </td>

                                            <td className={`px-2.5 py-2 text-right font-semibold whitespace-nowrap ${e.amendType === 'Substract' ? 'text-red-600 dark:text-red-400' : e.amendType === 'Add' ? 'text-green-600 dark:text-green-400' : 'text-gray-500'}`}>
                                                ₹{formatIndianCurrency(amount || 0)}
                                            </td>

                                            {/* Remarks */}
                                            <td className="px-1.5 py-1.5">
                                                <input type="text" placeholder="Notes…" className={`${inputCls} w-32`}
                                                    value={e.remarks ?? ''}
                                                    onChange={(ev) => handleFieldChange(item, 'remarks', ev.target.value)} />
                                            </td>

                                            <td className="px-2.5 py-2 text-gray-700 dark:text-gray-300 font-mono">{item.HSNCode || '-'}</td>

                                            {poData.GSTExist === 'Yes' && poData.StateStatus === 'Yes' && (
                                                <>
                                                    <td className="px-2.5 py-2 text-center">{item.CGSTPercent}</td>
                                                    <td className="px-2.5 py-2 text-center">{item.SGSTPercent}</td>
                                                </>
                                            )}
                                            {poData.GSTExist === 'Yes' && poData.StateStatus !== 'Yes' && (
                                                <td className="px-2.5 py-2 text-center">{item.IGSTPercent}</td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </SectionCard>
            )}

            {/* ── 4. Value Breakdown, Remarks & Upload ────────────────────── */}
            {gridHasData && (
                <SectionCard icon={IndianRupee} title="Amendment Value & Remarks" accent="amber">
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-amber-200 dark:border-amber-700">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Excess PO Value (+)</p>
                            <p className="text-sm font-bold text-green-600 dark:text-green-400">₹{formatIndianCurrency(totals.poPlusAmt)}</p>
                        </div>
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-amber-200 dark:border-amber-700">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Reduced PO Value (-)</p>
                            <p className="text-sm font-bold text-red-600 dark:text-red-400">₹{formatIndianCurrency(totals.poMinusAmt)}</p>
                        </div>
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-amber-200 dark:border-amber-700">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Previous PO Value</p>
                            <p className="text-sm font-bold text-gray-800 dark:text-gray-200">₹{formatIndianCurrency(totals.oldPOValue)}</p>
                        </div>
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-amber-200 dark:border-amber-700">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Amendment Required</p>
                            <p className="text-sm font-bold text-orange-600 dark:text-orange-400">₹{formatIndianCurrency(totals.amendDiffValue)}</p>
                        </div>
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-amber-200 dark:border-amber-700">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Revised PO Value</p>
                            <p className="text-sm font-bold text-indigo-600 dark:text-indigo-400">₹{formatIndianCurrency(totals.revisedValue)}</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label required>Remarks</Label>
                            <textarea rows={2} className={inputCls} placeholder="Enter reason for amendment…"
                                value={remarks} onChange={(e) => setRemarks(e.target.value)} />
                        </div>
                        <div>
                            <Label required>Amend Date</Label>
                            <CustomDatePicker value={amendDate} onChange={setAmendDate}
                                format="DD-MMM-YYYY" placeholder="Select Amend Date" maxDate={todayFormatted()} />
                        </div>
                    </div>

                    {uploadRequired && (
                        <div className="space-y-2">
                            <div className="flex items-center gap-2 text-xs font-semibold text-rose-700 dark:text-rose-400 bg-rose-50 dark:bg-rose-900/20 border border-rose-300 dark:border-rose-700 rounded-lg px-3 py-2">
                                <AlertTriangle className="w-4 h-4 shrink-0" />
                                Net amendment value exceeds the permitted limit (₹{formatIndianCurrency(uploadDocValue)}) — QCS PDF upload is mandatory.
                            </div>
                            <div className="flex items-center gap-4 flex-wrap">
                                <label className="flex items-center gap-2 px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium cursor-pointer">
                                    <Upload className="w-4 h-4" />
                                    {qcFile ? 'Change File' : 'Choose PDF File'}
                                    <input type="file" accept=".pdf" className="hidden"
                                        onChange={(e) => setQcFile(e.target.files[0] || null)} />
                                </label>
                                {qcFile ? (
                                    <div className="flex items-center gap-2 text-sm">
                                        <CheckCircle className="w-4 h-4 text-green-600" />
                                        <span className="text-gray-700 dark:text-gray-300">{qcFile.name}</span>
                                        <button onClick={() => setQcFile(null)}
                                            className="p-0.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500">
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                ) : (
                                    <span className="text-xs text-rose-600 dark:text-rose-400">No file chosen — PDF only</span>
                                )}
                            </div>
                        </div>
                    )}
                </SectionCard>
            )}

            {/* ── 5. Budget Check & Submit ─────────────────────────────────── */}
            {gridHasData && (
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-4 flex-wrap">
                            {totals.reduceBudgetAmt > 0 && budgetPassed && (
                                <span className="flex items-center gap-1.5 text-xs font-semibold text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700 px-3 py-1.5 rounded-full">
                                    <CheckCircle className="w-3.5 h-3.5" /> Budget OK
                                </span>
                            )}
                            {errors.budget && (
                                <span className="flex items-center gap-1.5 text-xs font-semibold text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 px-3 py-1.5 rounded-full">
                                    <AlertTriangle className="w-3.5 h-3.5" /> Budget Check Failed
                                </span>
                            )}
                            {totals.reduceBudgetAmt > 0 && (
                                <div className="text-sm text-gray-600 dark:text-gray-400">
                                    Extra Budget Needed: <span className="font-bold text-amber-700 dark:text-amber-400">
                                        ₹{formatIndianCurrency(totals.reduceBudgetAmt)}
                                    </span>
                                </div>
                            )}
                        </div>

                        <div className="flex items-center gap-3 flex-wrap">
                            {totals.reduceBudgetAmt > 0 && (
                                <button onClick={handleBudgetCheck}
                                    disabled={loading.budget}
                                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold disabled:opacity-50 transition-colors">
                                    {loading.budget ? <SpinnerIcon /> : <IndianRupee className="w-4 h-4" />}
                                    Check Budget
                                </button>
                            )}

                            <button onClick={handleSubmit}
                                disabled={loading.save || loading.budget}
                                className="flex items-center gap-2 px-5 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold disabled:opacity-50 transition-colors">
                                {(loading.save || loading.budget) ? <SpinnerIcon /> : <Send className="w-4 h-4" />}
                                {loading.save ? 'Saving…' : 'Submit'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Previous PO Details modal ────────────────────────────────── */}
            {showPreviousModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6 max-w-2xl w-full">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <Eye className="w-5 h-5 text-indigo-600" />
                                <h3 className="font-bold text-gray-900 dark:text-white">
                                    Previous PO History — {previewItem?.itemcode?.trim()} ({previewItem?.itemname})
                                </h3>
                            </div>
                            <button onClick={closePreviousModal} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
                                <X className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>

                        {loading.previousPODetails ? (
                            <div className="flex items-center gap-2 text-sm text-indigo-600 py-6 justify-center"><SpinnerIcon /> Loading…</div>
                        ) : previousPODetails.length === 0 ? (
                            <p className="text-sm text-gray-500 dark:text-gray-400 py-6 text-center">No previous PO history found for this item.</p>
                        ) : (
                            <div className="overflow-x-auto max-h-80 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="bg-gray-100 dark:bg-gray-700">
                                            <th className="px-3 py-2 text-left font-semibold">PO No</th>
                                            <th className="px-3 py-2 text-left font-semibold">Vendor</th>
                                            <th className="px-3 py-2 text-left font-semibold">PO Date</th>
                                            <th className="px-3 py-2 text-right font-semibold">Basic Price</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {previousPODetails.map((p, i) => (
                                            <tr key={i} className="border-t border-gray-100 dark:border-gray-700">
                                                <td className="px-3 py-2 font-mono">{p.PONo}</td>
                                                <td className="px-3 py-2">{p.VendorName}</td>
                                                <td className="px-3 py-2">{p.PODate}</td>
                                                <td className="px-3 py-2 text-right">₹{formatIndianCurrency(p.BasicPrice || 0)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SupplierPOAmend;
